import { useIdentityUsersTwoFactorSendMutation } from '../api/repositories/use-identity-users-two-factor-send.mutation';
import { useIdentityUsersTwoFactorSubmitMutation } from '../api/repositories/use-identity-users-two-factor-submit.mutation';
import { useAuth } from '../contexts/auth';
import { getRoleFromToken, getUserPermissions } from '../utils/token';
import { yupResolver } from '@hookform/resolvers/yup';
import { ApiError, TwoFactorType, UserService } from 'api/generated';
import clsx from 'clsx';
import { Role } from 'features/shared/model';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';

type FormValues = {
  code: string;
};

type TwoFactorState = {
  userId: number;
  twoFactorType: TwoFactorType;
  userEmail?: string;
  userPhone?: string;
  from: string;
};

const loginSchema = yup.object().shape({
  code: yup.string().min(4, 'Minimum 4 symbols').max(4, 'Maximum 4 symbols').required('Code is required')
});

const defaultValues: FormValues = {
  code: ''
};

export function TwoFactor() {
  const location = useLocation();
  const navigate = useNavigate();
  const [initState, setInitState] = useState<TwoFactorState>();

  const { saveAuth, setCurrentUser, setCurrentPermissions } = useAuth();
  const [formStatus, setFormStatus] = useState('');
  const { mutateAsync: login, isLoading: isSubmitting } = useIdentityUsersTwoFactorSubmitMutation();
  const { mutateAsync: sendTwoFactor } = useIdentityUsersTwoFactorSendMutation();

  useEffect(() => {
    setInitState(location?.state);
  }, []);

  useEffect(() => {
    if (initState) {
      sendTwoFactor({ userId: initState.userId });
    }
  }, [initState]);

  const {
    register,
    handleSubmit,
    formState: { isValid, isLoading: loading, errors, touchedFields }
  } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(loginSchema)
  });

  const onSubmit: SubmitHandler<FormValues> = (values: FormValues) => {
    login(
      {
        input: {
          userId: initState?.userId ?? 0,
          requestBody: values.code
        }
      },
      {
        onSuccess: (auth) => {
          if (auth.token) {
            const role: Role | null = getRoleFromToken(auth.token);
            saveAuth(auth);
            setCurrentUser(auth);
            UserService.getUsersPermissions(role as number).then((permissions) => {
              setCurrentPermissions(getUserPermissions(permissions));
            });
            navigate(`${initState?.from}`);
          } else if (auth.needPasswordChange) {
            navigate('/auth/change-password', {
              state: {
                userId: auth.userId,
                userEmail: auth.userEmail,
                userPhone: auth.userPhone,
                from: initState?.from
              }
            });
          } else {
            navigate('/error/500');
          }
        },
        onError: (error) => {
          let errorMessage = 'The login detail is incorrect';
          if (error instanceof ApiError) {
            if (error.body.error.message) errorMessage = error.body.error.message;
          }
          setFormStatus(errorMessage);
          console.error(error);
        }
      }
    );
  };

  if (!initState) return null;

  return (
    <form className="form w-100" onSubmit={handleSubmit(onSubmit)} noValidate id="kt_login_signin_form">
      {/* begin::Heading */}
      <div className="text-center mb-10">
        <h1 className="text-dark mb-3">Sign In to Advance Motors Admin</h1>
        {initState.twoFactorType == TwoFactorType.Email && (
          <div className="fs-5 mt-6">
            <p className="mb-1">We have sent a 4 digit code on you email</p>
            <p>
              address <b>{initState.userEmail}</b>
            </p>
          </div>
        )}
        {initState.twoFactorType == TwoFactorType.Sms && (
          <div className="fs-5 mt-6">
            <p className="mb-1">We have sent a 4 digit code via SMS</p>
            <p>
              on number <b>{initState?.userPhone?.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')}</b>
            </p>
          </div>
        )}
      </div>
      {/* begin::Heading */}

      {formStatus && (
        <div className="mb-lg-15 alert alert-danger">
          <div className="alert-text font-weight-bold">{formStatus}</div>
        </div>
      )}

      {/* begin::Form group */}
      <div className="fv-row mb-10">
        <label className="form-label fs-6 fw-bolder text-dark">
          {initState.twoFactorType == TwoFactorType.Email ? 'Email Code' : 'SMS Code'}
        </label>
        <input
          placeholder="Code"
          {...register('code')}
          className={clsx(
            'form-control form-control-lg form-control-solid',
            { 'is-invalid': touchedFields.code && errors.code },
            {
              'is-valid': touchedFields.code && !errors.code
            }
          )}
          type="text"
          name="code"
          autoComplete="off"
        />
        {touchedFields.code && errors.code && (
          <div className="fv-plugins-message-container">
            <span role="alert">{errors.code?.message}</span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Action */}
      <div className="text-center">
        <button
          type="submit"
          id="kt_sign_in_submit"
          className="btn btn-lg btn-primary w-100 mb-5"
          disabled={isSubmitting || !isValid}
        >
          {!loading && !isSubmitting && <span className="indicator-label">Submit</span>}
          {(loading || isSubmitting) && (
            <span className="indicator-progress" style={{ display: 'block' }}>
              Please wait...
              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}
    </form>
  );
}
